import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { message } from "antd";

import { IUsePhysicalInvestorDocuments } from "./IUsePhysicalInvestorDocuments";
import { ContractData, PhysicalInvestorDocumentsRequest, InvestorDocuments, InvestorDTO, Document, ContractDocumentsErrors, FundsOriginDocumentErrors } from "../../Models";

import { emptyContract, emptyPhysicalInvestorDocumentsRequest, emptyInvestorDocuments, emptyInvestor } from "../../Assets/Data/EmptyValues";
import { FileTypes } from "../../Assets/Enums";
import { getFundSourceDocumentOptions } from "../../Assets/OptionsTranslations";

export const usePhysicalInvestorDocuments = ({
	translations,
	contractAPI,
	investorsAPI,
	documentsAPI,
	unlockNextStep,
}: IUsePhysicalInvestorDocuments) => {
	const [searchParams] = useSearchParams();
	const location = useLocation();

	const [loading, setLoading] = useState<boolean>(false);
	const [noContent, setNoContent] = useState<boolean>(false);
	const [contractData, setContractData] = useState<ContractData>(emptyContract);
	const [errors, setErrors] = useState<ContractDocumentsErrors>({});
	const [investors, setInvestors] = useState<InvestorDTO[]>([]);
	const [documentsData, setDocumentsData] = useState<PhysicalInvestorDocumentsRequest>(emptyPhysicalInvestorDocumentsRequest);
	const [showMandatoryErrors, setShowMandatoryErrors] = useState<boolean>(false);
	
	useEffect(() => {
		setInvestors([emptyInvestor]);
		setContractData(emptyContract);
		setShowMandatoryErrors(false);
	}, [location.pathname]);

	useEffect(() => {
		if (searchParams.has('ContractId')) {
			configureInitialData(Number(searchParams.get('ContractId')));
		} else if (searchParams.has('hash')) {
			const hash = searchParams.get('hash');
			const getContractIdByHash = async(hash: string) => {
				return await contractAPI.getContractByHash(hash).then((res: any) => {
					if (res.success) {
						configureInitialData(Number(res.data));
					} else {
						message.error(translations.messageError);
						setNoContent(true);
					}
				})
				.catch((err: any) => {
					message.error(translations.messageError);
				})
			}
		
			getContractIdByHash(hash!);
		} else {
			setInvestors([emptyInvestor]);
		}
	}, []);

	const buildInitialErrors = (docsData: PhysicalInvestorDocumentsRequest): ContractDocumentsErrors => {
		const initialErrors: ContractDocumentsErrors = {};
		
		const fundsOriginErrors: { [key: string]: FundsOriginDocumentErrors } = {};
		(docsData.fundsOriginDocuments || []).forEach((doc) => {
			if (doc.type) {
				fundsOriginErrors[doc.type] = {};
			}
		});
		initialErrors.contract = fundsOriginErrors;
		
		return initialErrors;
	};

	const configureInitialData = async(ContractId: number) => {
		setLoading(true);
		setErrors({});
		setShowMandatoryErrors(false);
		await contractAPI.getContract(ContractId).then(async(contractResponse: any) => {
			if (contractResponse.success) {
				const contract = contractResponse.data;
				
				let investorsData: InvestorDTO[] = [];
				
				await investorsAPI.getInvestors(ContractId).then(async(resInvestors: any) => {
					if (resInvestors.success) {
						investorsData = resInvestors.data?.map((investor: InvestorDTO) => {
							if(investor.dni) investor.documentType = 'DNI';
							if(investor.nif) investor.documentType = 'NIF';
							if(investor.passport) investor.documentType = 'Passport';
							if(investor.residenceCard) investor.documentType = 'Residence Card';
							return investor;
						});
						
						const initialDocsData: PhysicalInvestorDocumentsRequest = {
							contractId: ContractId,
							bankCertificate: null,
							fundsOriginDocuments: [],
							investorDocuments: investorsData.map(() => ({ ...emptyInvestorDocuments }))
						};
						
						setContractData(contract);
						setInvestors(investorsData);
						setDocumentsData(initialDocsData);
					}
					
					await documentsAPI.getPhysicalInvestorDocuments(ContractId).then((resDocuments: any) => {
						if (resDocuments.success && resDocuments.data && investorsData.length > 0) {
							const docsData: PhysicalInvestorDocumentsRequest = {
								contractId: ContractId,
								bankCertificate: null,
								fundsOriginDocuments: [],
								investorDocuments: investorsData.map(() => ({ ...emptyInvestorDocuments }))
							};

							setDocumentsData(resDocuments.data);
							const initialErrors = buildInitialErrors(docsData);
							setErrors(initialErrors);
						}
						setLoading(false);
					});
				});
			} else {
				setContractData({ ...contractData });
				setNoContent(true);
				setLoading(false);
			}
		}).catch((err: any) => {
			console.log(err);
			setLoading(false);
		});
	};

	const handleUpdateIdDocument = (index: number, source: string) => (fileUrl: string) => {
		const updatedDocsData = { 
			...documentsData,
			investorDocuments: [...documentsData.investorDocuments]
		};
		const currentDoc = updatedDocsData.investorDocuments[index]?.idDocument || { url: '', type: FileTypes[source] || source, status: '', idRegistry: 0 };
		updatedDocsData.investorDocuments[index] = {
			...updatedDocsData.investorDocuments[index],
			idDocument: {
				...currentDoc,
				url: fileUrl,
				status: 'Draft',
				idRegistry: investors[index]?.id || 0,
			},
		};
		setDocumentsData(updatedDocsData);
		setErrors(buildInitialErrors(updatedDocsData));
	};

	const getFundSourceType = (source: string) => {
		const normalizedSource = source.trim();
		return FileTypes[normalizedSource] || normalizedSource;
	};

	const getExistingFundSourceDocumentIndex = (documents: Document[], source: string) => {
		const normalizedSource = source.trim();
		const normalizedType = getFundSourceType(source);
		const matchingDocuments = documents
			.map((doc, index) => ({ doc, index }))
			.filter(({ doc }) => doc.type === normalizedType || doc.type === normalizedSource);

		return matchingDocuments.find(({ doc }) => Boolean(doc.url))?.index ?? matchingDocuments[0]?.index ?? -1;
	};

	const deduplicateFundSourceDocuments = (documents: Document[], source: string, keepIndex: number) => {
		const normalizedSource = source.trim();
		const normalizedType = getFundSourceType(source);
		return documents.filter((doc, index) => {
			if (index === keepIndex) {
				return true;
			}
			return doc.type !== normalizedType && doc.type !== normalizedSource;
		});
	};

	const handleUpdateFundSourceDocument = (source: string) => (fileUrl: string) => {
		const normalizedType = getFundSourceType(source);
		const updatedDocsData = { 
			...documentsData,
			fundsOriginDocuments: [...documentsData.fundsOriginDocuments]
		};
		const existingDocIndex = getExistingFundSourceDocumentIndex(updatedDocsData.fundsOriginDocuments, source);
		if (existingDocIndex !== -1) {
			const existingDoc = updatedDocsData.fundsOriginDocuments[existingDocIndex];
			updatedDocsData.fundsOriginDocuments[existingDocIndex] = {
				...existingDoc,
				url: fileUrl,
				status: 'Draft',
				type: normalizedType,
			};
			updatedDocsData.fundsOriginDocuments = deduplicateFundSourceDocuments(updatedDocsData.fundsOriginDocuments, source, existingDocIndex);
		} else {
			updatedDocsData.fundsOriginDocuments.push({ url: fileUrl, type: normalizedType, status: 'Draft', idRegistry: 0 });
		}
		setDocumentsData(updatedDocsData);
		setErrors(buildInitialErrors(updatedDocsData));
	};

	const handleUpdateDocument = (investorIndex: number, fileType: string) => (fileUrl: string) => {
		if (fileType === 'bankCertificate') {
			const currentDoc = documentsData.bankCertificate || { url: '', type: FileTypes.bankCertificate, status: '', idRegistry: 0 };
			const updatedDocsData = {
				...documentsData,
				bankCertificate: {
					...currentDoc,
					url: fileUrl,
					status: 'Draft',
					idRegistry: 0,
					type: 'Bank Certificate',
				}
			};
			setDocumentsData(updatedDocsData);
			setErrors(buildInitialErrors(updatedDocsData));
		} else {
			const updatedDocsData = {
				...documentsData,
				investorDocuments: [...documentsData.investorDocuments]
			};
			const investorDocs = updatedDocsData.investorDocuments[investorIndex];
			const existingDoc = investorDocs[fileType as keyof InvestorDocuments];
			const currentDoc = (existingDoc ?? { url: '', type: FileTypes[fileType as keyof typeof FileTypes], status: '', idRegistry: 0 }) as Document;
			let idRegistry = investors[investorIndex]?.id ?? currentDoc.idRegistry ?? 0;
			if (fileType === 'w8BEN' || fileType === 'w9' || fileType === 'tin' || fileType === 'taxResidenceCertificate') idRegistry = 0;
			
			updatedDocsData.investorDocuments[investorIndex] = {
				...investorDocs,
				[fileType]: {
					...currentDoc,
					url: fileUrl,
					status: 'Draft',
					idRegistry: idRegistry,
					type: FileTypes[fileType as keyof typeof FileTypes],
				},
			};

			setDocumentsData(updatedDocsData);
			setErrors(buildInitialErrors(updatedDocsData));
		}
	};

	const handleDocumentTypeChange = (source: string) => (value: string) => {
		const normalizedType = getFundSourceType(source);
		const updatedDocsData = {
			...documentsData,
			fundsOriginDocuments: [...documentsData.fundsOriginDocuments]
		};
		const existingDocIndex = getExistingFundSourceDocumentIndex(updatedDocsData.fundsOriginDocuments, source);
		if (existingDocIndex === -1) {
			return;
		}

		const existingDoc = updatedDocsData.fundsOriginDocuments[existingDocIndex];
		updatedDocsData.fundsOriginDocuments[existingDocIndex] = {
			...existingDoc,
			type: normalizedType,
			description: value,
			status: 'Draft'
		};
		updatedDocsData.fundsOriginDocuments = deduplicateFundSourceDocuments(updatedDocsData.fundsOriginDocuments, source, existingDocIndex);
		setDocumentsData(updatedDocsData);
		setErrors(buildInitialErrors(updatedDocsData));
	};

	const splitDocumentsByPersistence = (docsData: PhysicalInvestorDocumentsRequest) => {
		let docsToSave: PhysicalInvestorDocumentsRequest | null = {
			contractId: docsData.contractId,
			bankCertificate: null,
			fundsOriginDocuments: [],
			investorDocuments: []
		};
		let docsToUpdate: PhysicalInvestorDocumentsRequest |null = {
			contractId: docsData.contractId,
			bankCertificate: null,
			fundsOriginDocuments: [],
			investorDocuments: []
		};

		// Bank Certificate
		if (docsData.bankCertificate) {
			if (docsData.bankCertificate.id) {
				docsToUpdate.bankCertificate = docsData.bankCertificate;
			} else if (docsData.bankCertificate.url) {
				docsToSave.bankCertificate = docsData.bankCertificate;
			}
		}

		// Funds Origin Documents
		(docsData.fundsOriginDocuments || []).forEach((doc) => {
			if (doc.id) {
				docsToUpdate?.fundsOriginDocuments.push(doc);
			} else if (doc.url) {
				docsToSave?.fundsOriginDocuments.push(doc);
			}
		});

		// Investor Documents
		docsData.investorDocuments.forEach((investorDoc) => {
			const newInvestorDoc: InvestorDocuments = {
				investorId: investorDoc.investorId,
				idDocument: null,
				w8BEN: null,
				w9: null,
				tin: null,
				taxResidenceCertificate: null
			};
			const existingInvestorDoc: InvestorDocuments = {
				investorId: investorDoc.investorId,
				idDocument: null,
				w8BEN: null,
				w9: null,
				tin: null,
				taxResidenceCertificate: null
			};

			if (investorDoc.idDocument) {
				if (investorDoc.idDocument.id) {
					existingInvestorDoc.idDocument = investorDoc.idDocument;
				} else if (investorDoc.idDocument.url) {
					newInvestorDoc.idDocument = investorDoc.idDocument;
				}
			}

			if (investorDoc.w8BEN) {
				if (investorDoc.w8BEN.id) {
					existingInvestorDoc.w8BEN = investorDoc.w8BEN;
				} else if (investorDoc.w8BEN.url) {
					newInvestorDoc.w8BEN = investorDoc.w8BEN;
				}
			}

			if (investorDoc.w9) {
				if (investorDoc.w9.id) {
					existingInvestorDoc.w9 = investorDoc.w9;
				} else if (investorDoc.w9.url) {
					newInvestorDoc.w9 = investorDoc.w9;
				}
			}

			if (investorDoc.tin) {
				if (investorDoc.tin.id) {
					existingInvestorDoc.tin = investorDoc.tin;
				} else if (investorDoc.tin.url) {
					newInvestorDoc.tin = investorDoc.tin;
				}
			}

			if (investorDoc.taxResidenceCertificate) {
				if (investorDoc.taxResidenceCertificate.id) {
					existingInvestorDoc.taxResidenceCertificate = investorDoc.taxResidenceCertificate;
				} else if (investorDoc.taxResidenceCertificate.url) {
					newInvestorDoc.taxResidenceCertificate = investorDoc.taxResidenceCertificate;
				}
			}

			const hasNewDocs = Boolean(
				newInvestorDoc.idDocument ||
				newInvestorDoc.w8BEN ||
				newInvestorDoc.w9 ||
				newInvestorDoc.tin ||
				newInvestorDoc.taxResidenceCertificate
			);

			const hasExistingDocs = Boolean(
				existingInvestorDoc.idDocument ||
				existingInvestorDoc.w8BEN ||
				existingInvestorDoc.w9 ||
				existingInvestorDoc.tin ||
				existingInvestorDoc.taxResidenceCertificate
			);

			if (hasNewDocs) {
				docsToSave?.investorDocuments.push(newInvestorDoc);
			}
			if (hasExistingDocs) {
				docsToUpdate?.investorDocuments.push(existingInvestorDoc);
			}
		});

		return { docsToSave, docsToUpdate };
	};

	const areAllDocumentsUploaded = () => {
		if (documentsData.investorDocuments.length === 0) return false;
		
		const allInvestorsValid = documentsData.investorDocuments.every((investorDocs: InvestorDocuments, index: number) => {
			const hasIdDocument = Boolean(investorDocs.idDocument?.url && investorDocs.idDocument.type);

			let hasW8BEN = true;
			let hasW9 = true;
			let hasTIN = true;
			let hasTaxResidenceCertificate = true;
			if (investors[index]?.countryTaxResidence !== 'España') {
				hasW8BEN = !Boolean(investors[index]?.american) ? Boolean(investorDocs.w8BEN?.url) : true;
  				hasW9 = Boolean(investors[index]?.american) ? Boolean(investorDocs.w9?.url) : true;
				hasTaxResidenceCertificate = Boolean(investorDocs.taxResidenceCertificate?.url);
			}

			return hasIdDocument && hasW8BEN && hasW9 && hasTIN && hasTaxResidenceCertificate;
		});

		const hasBankCertificate = Boolean(documentsData.bankCertificate?.url && documentsData.bankCertificate.type);
		const fundsDocs = documentsData.fundsOriginDocuments || [];
		const uniqueSources = getAllUniqueFundSources();
		const hasFundsDocuments =
			uniqueSources.length > 0 &&
			uniqueSources.every((source) => {
				const normalizedType = getFundSourceType(source);
				const sourceDocuments = fundsDocs.filter((d: Document) => d.type === normalizedType || d.type === source.trim());
				return sourceDocuments.some((doc: Document) => Boolean(doc?.url && doc?.description));
			});

		return allInvestorsValid && hasBankCertificate && hasFundsDocuments;
	};

	const getAllUniqueFundSources = (): string[] => {
		let sources: string[] = [];
		investors.map((investor) => 
			investor.source?.split(';').map((s) => {
				const source = s.trim();
				if (source && !sources.includes(source)) {
					sources.push(s.trim());
				}
			})
		);
		return sources;
	};

	const hasUrlAndType = (document?: Document | null) => Boolean(document?.url && document?.type);

	const isMandatoryInvestorDocumentMissing = (
		investorIndex: number,
		fileType: 'idDocument' | 'w8BEN' | 'w9' | 'taxResidenceCertificate'
	) => {
		if (!showMandatoryErrors) {
			return false;
		}

		const investor = investors[investorIndex];
		const investorDocs = documentsData.investorDocuments[investorIndex];
		if (!investor || !investorDocs) {
			return false;
		}

		if (fileType === 'idDocument') {
			return !hasUrlAndType(investorDocs.idDocument);
		}

		if (investor.countryTaxResidence === 'España') {
			return false;
		}

		if (fileType === 'w8BEN') {
			return !Boolean(investor.american) && !Boolean(investorDocs.w8BEN?.url);
		}

		if (fileType === 'w9') {
			return Boolean(investor.american) && !Boolean(investorDocs.w9?.url);
		}

		if (fileType === 'taxResidenceCertificate') {
			return !Boolean(investorDocs.taxResidenceCertificate?.url);
		}

		return false;
	};

	const isMandatoryBankCertificateMissing = () => (
		showMandatoryErrors && !hasUrlAndType(documentsData.bankCertificate)
	);

	const isMandatoryFundSourceDocumentMissing = (source: string) => {
		if (!showMandatoryErrors) {
			return false;
		}

		const normalizedSource = source.trim();
		const normalizedType = getFundSourceType(source);
		const document = (documentsData.fundsOriginDocuments || []).find(
			(doc: Document) => doc.type === normalizedType || doc.type === normalizedSource
		);

		return !Boolean(document?.url);
	};

	const getFundsOriginOptions = (source: string) => {
		const normalizedSource = source.trim();
		const normalizedSourceType = FileTypes[normalizedSource] || normalizedSource;
		const documentOptions = getFundSourceDocumentOptions(translations, normalizedSourceType);
		
		return documentOptions.map((option: string) => {
			return {
				label: option,
				value: option,
			}
		});
	};

	const validateDocumentsData = () => {
		let isValid = true;
		const contractDocumentsErrors: ContractDocumentsErrors = buildInitialErrors(documentsData);
		
		(documentsData.fundsOriginDocuments || []).forEach((fundsOriginDoc: Document) => {
			if (!contractDocumentsErrors.contract) {
				contractDocumentsErrors.contract = {};
			}
			const sourceKey = fundsOriginDoc.type || 'unknown';
			if (!contractDocumentsErrors.contract[sourceKey]) {
				contractDocumentsErrors.contract[sourceKey] = {};
			}
			if (!fundsOriginDoc.url && fundsOriginDoc.description) {
				contractDocumentsErrors.contract[sourceKey].fundsOriginFileNotUploaded = true;
				isValid = false;
			}
			if (fundsOriginDoc.url && !fundsOriginDoc.description) {
				contractDocumentsErrors.contract[sourceKey].fundsOriginTypeNotSelected = true;
				isValid = false;
			}
		});
		
		setErrors(contractDocumentsErrors);
		return isValid;
	};

	const saveData = async () => {
		setLoading(true);
		setShowMandatoryErrors(true);
		const isValid = validateDocumentsData();
		if (!isValid) {
			setLoading(false);
			return;
		}

		const contractId = Number(searchParams.get('ContractId'));

		const { docsToSave, docsToUpdate } = splitDocumentsByPersistence(documentsData);

		try {
			const responses: any[] = [];
			const hasDocsToSave = docsToSave && (docsToSave.bankCertificate || docsToSave.fundsOriginDocuments.length > 0 || docsToSave.investorDocuments.length > 0);
			const hasDocsToUpdate = docsToUpdate && (docsToUpdate.bankCertificate || docsToUpdate.fundsOriginDocuments.length > 0 || docsToUpdate.investorDocuments.length > 0);
			
			if (hasDocsToSave) {
				responses.push(await documentsAPI.savePhysicalInvestorDocuments(docsToSave));
			}
			if (hasDocsToUpdate) {
				responses.push(await documentsAPI.updatePhysicalInvestorDocuments(docsToUpdate));
			}
			const hasErrors = responses.some((res) => !res?.success);
			if (hasErrors) {
				message.error(translations.messageError);
			} else {
				message.success(translations.datosGuardados);
				if (responses.length > 0) {
					await configureInitialData(contractId);
				}
				if (areAllDocumentsUploaded()) {
					unlockNextStep(contractId);
				}
			}
		} catch (error) {
			console.error(error);
			message.error(translations.messageError);
		} finally {
			setLoading(false);
		}
	};

	return {
		loading,
		noContent,
		contractData,
		investors,
		documentsData,
		errors,
		handleUpdateIdDocument,
		handleUpdateFundSourceDocument,
		handleDocumentTypeChange,
		saveData,
		areAllDocumentsUploaded,
		getFundsOriginOptions,
		handleUpdateDocument,
		getAllUniqueFundSources,
		isMandatoryInvestorDocumentMissing,
		isMandatoryBankCertificateMissing,
		isMandatoryFundSourceDocumentMissing,
	};
}
